import { ImagesHeaderSpiritoryLogoSvg } from '@/assets/images_header.assets';
import Logo from '@/public/logo.svg';
import Image from 'next/image';
import Container from '../Container';
import { useRef, useState } from 'react';
import clsx from 'clsx';
import InstantSearchAlgolia from '../InstantSearchAlgolia';
import HeaderHits from './HeaderHits';
import MobileMenu from './MobileMenu/MobileMenu';
import UserMenu from './UserMenu/UserMenu';
import DefaultLink from '../common/default/DefaultLink';
import MenuSearch from './HeaderSearch';
import { twMerge } from 'tailwind-merge';
import HeaderUserActions from './HeaderUserDropdown/HeaderUserActions';
import Link from 'next/link';
import { useCtaCount, useNotifications } from '@/useQuery/queries/notifications';
import { UseQueryResult } from '@tanstack/react-query';
import 'react-loading-skeleton/dist/skeleton.css';
import HeaderSearchSkeleton from '../common/Skeleton/HeaderSearchSkeleton';
import useHasMounted from '@/hooks/useHasMounted';

const ResponsiveHeader = () => {
  const hasMounted = useHasMounted();
  const ctaCountQuery = useCtaCount();
  const notificationsQuery = useNotifications();
  const [searchOnFocus, setSearchOnFocus] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const mobileMenuHitsRef = useRef<HTMLDivElement>(null);

  return (
    <>
      {/* Mobile Header */}
      <Container
        className={clsx(
          'tw-block tw-bg-white lg:tw-hidden',
          searchOnFocus && searchValue?.trim().length > 0
            ? 'tw-fixed tw-inset-0 tw-z-50 tw-h-screen'
            : 'tw-sticky tw-left-0 tw-top-0 tw-z-[9999]'
        )}
      >
        <div className="tw-py-2">
          <div className="tw-flex tw-w-full tw-items-center tw-justify-between child:tw-w-full">
            <div className="tw-flex tw-items-center tw-justify-start">
              <MobileMenu />
            </div>
            <Link href="/" className="tw-flex tw-items-center tw-justify-center">
              <Image
                src={ImagesHeaderSpiritoryLogoSvg}
                alt="spiritory logo"
                width={156}
                height={39}
              />
            </Link>
            <div className="tw-flex tw-items-center tw-justify-end">
              <UserMenu
                ctaCountQuery={ctaCountQuery}
                notificationsQuery={
                  notificationsQuery as unknown as UseQueryResult<Notification[] | null>
                }
              />
            </div>
          </div>
          {hasMounted ? (
            <InstantSearchAlgolia>
              <MenuSearch
                setSearchOnFocus={setSearchOnFocus}
                searchOnFocus={searchOnFocus}
                ref={mobileMenuHitsRef}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                mobile={true}
              />
              <div ref={mobileMenuHitsRef}>
                <HeaderHits searchOnFocus={searchOnFocus} searchValue={searchValue} />
              </div>
            </InstantSearchAlgolia>
          ) : (
            <HeaderSearchSkeleton />
          )}
        </div>
      </Container>

      {/* Desktop Header */}
      <header className="tw-hidden lg:tw-block">
        <Container className="tw-py-2">
          <div className="tw-flex tw-items-center tw-gap-3">
            {/* Logo */}
            <div>
              <DefaultLink href="/" aria-label="Go to home page">
                <Image
                  src={Logo}
                  alt="Spiritory Logo"
                  width={229}
                  height={52}
                  className="tw-object-contain tw-object-center"
                />
              </DefaultLink>
            </div>

            {/* Search */}
            <div className="tw-flex-grow">
              {hasMounted ? (
                <InstantSearchAlgolia>
                  <div className="tw-relative">
                    <MenuSearch
                      setSearchOnFocus={setSearchOnFocus}
                      searchOnFocus={searchOnFocus}
                      ref={mobileMenuHitsRef}
                      searchValue={searchValue}
                      setSearchValue={setSearchValue}
                    />
                    <div
                      ref={mobileMenuHitsRef}
                      className={twMerge('tw-absolute tw-left-0 tw-top-full tw-z-50 tw-w-full')}
                    >
                      <HeaderHits searchOnFocus={searchOnFocus} searchValue={searchValue} />
                    </div>
                  </div>
                </InstantSearchAlgolia>
              ) : (
                <HeaderSearchSkeleton />
              )}
            </div>

            {/* User Actions */}
            <div className="tw-flex-shrink-0">
              <HeaderUserActions />
            </div>
          </div>
        </Container>
      </header>
    </>
  );
};

export default ResponsiveHeader;
